/**
 * Utility functions for image handling and conversion
 */

/**
 * Convert any image file or blob to PNG format
 * @param imageFile - The image file or blob to convert
 * @param maxWidth - Optional maximum width (maintains aspect ratio)
 * @param maxHeight - Optional maximum height (maintains aspect ratio)
 * @returns Promise resolving to a PNG File object
 */
export async function convertImageToPNG(
  imageFile: File | Blob,
  maxWidth?: number,
  maxHeight?: number
): Promise<File> {
  return new Promise((resolve, reject) => {
    // Create image element
    const img = new Image();
    const blobUrl = URL.createObjectURL(imageFile);

    img.onload = () => {
      try {
        // Calculate dimensions
        let width = img.width;
        let height = img.height;

        // Resize if max dimensions provided
        if (maxWidth && width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (maxHeight && height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        // Create canvas and draw image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Convert to PNG blob
        canvas.toBlob(
          (pngBlob) => {
            URL.revokeObjectURL(blobUrl);
            
            if (!pngBlob) {
              reject(new Error('Failed to convert image to PNG'));
              return;
            }

            // Create File object with PNG extension
            const originalName = imageFile instanceof File ? imageFile.name : 'image';
            const nameWithoutExt = originalName.replace(/\.[^/.]+$/, '');
            const pngFile = new File([pngBlob], `${nameWithoutExt}.png`, {
              type: 'image/png',
              lastModified: Date.now(),
            });

            resolve(pngFile);
          },
          'image/png',
          1.0 // Maximum quality
        );
      } catch (error) {
        URL.revokeObjectURL(blobUrl);
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = blobUrl;
  });
}

/**
 * Convert a blob to PNG and return as blob (for clipboard operations)
 * @param imageBlob - The image blob to convert
 * @returns Promise resolving to a PNG Blob
 */
export async function convertBlobToPNG(imageBlob: Blob): Promise<Blob> {
  const file = await convertImageToPNG(imageBlob);
  return file;
}
